import React, { Suspense, lazy } from 'react';
// Ensure BrowserRouter (aliased as Router) is NOT imported here
import { Routes, Route, Navigate } from 'react-router-dom'; 
import { useAuth } from '../hooks/useAuth';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { ChallengeNotificationProvider } from '../contexts/ChallengeNotificationContext';
import ErrorBoundary from './ErrorBoundary';
import ConversationWrapper from './ConversationWrapper';
import LoginForm from './auth/LoginForm';
import SignupForm from './auth/SignupForm';
import AuthCallback from './auth/AuthCallback';
import EmailVerification from './auth/EmailVerification';
import ResetPassword from './auth/ResetPassword';
import SubscriptionPlans from './payment/SubscriptionPlans';
import Subscribe from './Subscribe';
import PaymentSuccess from './payment/PaymentSuccess';
import Settings from './Settings';
import VoiceSettings from './VoiceSettings';
import Referrals from './Referrals';
import FAQ from './FAQ';
import DeactivateAccount from './DeactivateAccount';
import Journal from './Journal';
import InstantWellness from './InstantWellness';
import IntelligentMeditation from './IntelligentMeditation';
import ProgressDashboard from './ProgressDashboard';
import GetTokens from './GetTokens';
import HumanTherapists from './HumanTherapists';
import Leaderboard from './Leaderboard'; // Add leaderboard import
import Challenges from './Challenges';
import MindGames from './MindGames';
import MemoryGamePage from './MemoryGamePage';
import TermsAndConditions from './legal/TermsAndConditions';
import LandingPage from './LandingPage';
import AnxietyLandingPage from './AnxietyLandingPage';
import TransformationLandingPage from './TransformationLandingPage';
// Import mobile compatibility utilities
import '../utils/mobileCompatibility';
import RelationshipsLandingPage from './RelationshipsLandingPage';
import CareerLandingPage from './CareerLandingPage';
import MaleAnxietyLandingPage from './MaleAnxietyLandingPage';
import MaleTransformationLandingPage from './MaleTransformationLandingPage';
import MobileOptimizedLanding from './MobileOptimizedLanding';
import BurnoutLandingPage from './BurnoutLandingPage';
import LoadingScreen from './common/LoadingScreen';

import { Box } from '@mui/material';
import '../styles/App.css';

const Quiz = lazy(() => import('./auth/Quiz'));

const ProtectedRoute = ({ children }) => {
  const { session, loading: authLoading } = useAuth();
  
  if (authLoading) {
    return <LoadingScreen message="Checking authentication..." />;
  }
  
  if (!session?.user) { 
    return <Navigate to="/login" replace />;
  }
  
  // Check if this might be a fresh email verification
  // If user was created very recently and landed here, they might have bypassed the auth callback
  const userCreatedRecently = session.user?.created_at && 
    (new Date() - new Date(session.user.created_at)) < 10 * 60 * 1000; // 10 minutes
  
  const currentUrl = window.location.href;
  const hasAuthTokensInUrl = currentUrl.includes('access_token') || 
                            currentUrl.includes('refresh_token') ||
                            window.location.hash.includes('access_token');
  
  // For fresh email verification, redirect to quiz to complete onboarding
  if (userCreatedRecently && hasAuthTokensInUrl) {
    console.log('ProtectedRoute: Fresh email verification detected, redirecting to quiz');
    return <Navigate to="/quiz" replace />;
  }
  
  return children;
};

// Route that requires both authentication AND active subscription
const SubscriptionProtectedRoute = ({ children }) => {
  const { session, loading: authLoading, profile, subscriptionStatus } = useAuth();
  
  console.log('🔍 SubscriptionProtectedRoute render:', {
    authLoading,
    hasSession: !!session?.user,
    userId: session?.user?.id,
    hasProfile: !!profile,
    profileQuizCompleted: profile?.quiz_completed,
    subscriptionStatus,
    pathname: window.location.pathname
  });
  
  if (authLoading) {
    console.log('SubscriptionProtectedRoute: Auth still loading...');
    return <LoadingScreen message="Checking authentication..." />;
  }
  
  if (!session?.user) { 
    console.log('SubscriptionProtectedRoute: No session user, redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  // Check if this might be a fresh email verification (user has session but no profile yet)
  // This can happen if users land directly on protected routes after email verification
  const userCreatedRecently = session.user?.created_at && 
    (new Date() - new Date(session.user.created_at)) < 10 * 60 * 1000; // 10 minutes
  
  const currentUrl = window.location.href;
  const hasAuthTokensInUrl = currentUrl.includes('access_token') || 
                            currentUrl.includes('refresh_token') ||
                            window.location.hash.includes('access_token');
  
  // For fresh email verification, redirect to quiz instead of login
  if (userCreatedRecently && hasAuthTokensInUrl && !profile) {
    console.log('SubscriptionProtectedRoute: Fresh email verification detected, redirecting to quiz');
    return <Navigate to="/quiz" replace />;
  }
  
  // CRITICAL: If no profile exists, user must go through quiz first
  if (!profile) {
    console.log('SubscriptionProtectedRoute: No profile found, redirecting to quiz');
    return <Navigate to="/quiz" replace />;
  }
  
  // Check if this is a token purchase success - these users should be allowed through
  const urlParams = new URLSearchParams(window.location.search);
  const isTokenPurchase = urlParams.get('token_purchase') === 'success';
  
  // Check if quiz is completed BEFORE waiting for subscription status
  // This prevents users from getting stuck on loading screen when they need to complete quiz
  const quizCompleted = profile.quiz_completed === true;
  console.log('SubscriptionProtectedRoute: Quiz completion check:', {
    quiz_completed_raw: profile.quiz_completed,
    quiz_completed_type: typeof profile.quiz_completed,
    quizCompleted: quizCompleted,
    isTokenPurchase: isTokenPurchase
  });
  
  if (!quizCompleted) {
    console.log('SubscriptionProtectedRoute: Quiz not completed, redirecting to quiz. Current value:', profile.quiz_completed);
    return <Navigate to="/quiz" replace />;
  }

  // For token purchases, allow through immediately after quiz completion
  if (isTokenPurchase) {
    console.log('SubscriptionProtectedRoute: Token purchase detected, allowing access regardless of subscription status');
    return children;
  }

  // Wait for subscription data to load only AFTER confirming quiz is completed and it's not a token purchase
  if (subscriptionStatus === null) {
    console.log('SubscriptionProtectedRoute: Quiz completed, waiting for subscription status to load...');
    return <LoadingScreen message="Loading subscription status..." />;
  }
  
  // Check subscription status - ONLY active subscriptions allowed
  console.log('🔍 SubscriptionProtectedRoute: Detailed subscription check:', {
    subscriptionStatus,
    profile_subscription_status: profile.subscription_status,
    subscription_ends_at: profile.subscription_ends_at,
    isExpired: profile.subscription_ends_at ? new Date(profile.subscription_ends_at) < new Date() : 'no_end_date'
  });
  
  if (subscriptionStatus !== 'active') {
    console.log('SubscriptionProtectedRoute: Subscription not active:', subscriptionStatus, 'Profile subscription_status:', profile.subscription_status);
    const message = subscriptionStatus === 'expired'
      ? 'Your subscription has expired. Please renew to continue.'
      : 'Please complete your subscription to access the platform.';
    
    return <Navigate to="/subscribe" state={{ message }} replace />;
  }
  
  // All checks passed - user has profile, completed quiz, and active subscription
  console.log('SubscriptionProtectedRoute: All checks passed, allowing access to protected route');
  return children;
};

const App = () => {
  // Always call the hook, but let the hook handle session checks internally
  useSessionTimeout();
  
  return (
    <ChallengeNotificationProvider>
      <ErrorBoundary>
        {/* The <Router> component should NOT be here. It's provided by src/index.js */}
        <Box className="app" sx={{ minHeight: '100vh' }}>
          <Suspense fallback={<LoadingScreen message="Loading page..." />}>
            <Routes>
              <Route path="/login" element={<LoginForm />} />
              <Route path="/signup" element={<SignupForm />} />
              <Route path="/verify-email" element={<EmailVerification />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route 
                path="/quiz" 
                element={
                  <ProtectedRoute>
                    <Quiz />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/personalized-insights" 
                element={<Navigate to="/subscribe" replace />} 
              />
              <Route 
                path="/subscribe" 
                element={
                  <ProtectedRoute>
                    <Subscribe />
                  </ProtectedRoute>
                } 
              />
              <Route path="/subscription-plans" element={<SubscriptionPlans />} /> 
              <Route 
                path="/get-tokens" 
                element={
                  <ProtectedRoute>
                    <GetTokens />
                  </ProtectedRoute>
                } 
              />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route 
                path="/chat" 
                element={
                  <SubscriptionProtectedRoute>
                    <ConversationWrapper />
                  </SubscriptionProtectedRoute>
                } 
              />
              <Route 
                path="/conversation" 
                element={
                  <SubscriptionProtectedRoute>
                    <ConversationWrapper />
                  </SubscriptionProtectedRoute>
                } 
              />
              <Route 
                path="/therapy" 
                element={
                  <SubscriptionProtectedRoute>
                    <ConversationWrapper />
                  </SubscriptionProtectedRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/voice-settings" 
                element={
                  <ProtectedRoute>
                    <VoiceSettings />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/referrals" 
                element={
                  <ProtectedRoute>
                    <Referrals />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/faq" 
                element={
                  <ProtectedRoute>
                    <FAQ />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/deactivate-account" 
                element={
                  <ProtectedRoute>
                    <DeactivateAccount />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/journal" 
                element={
                  <SubscriptionProtectedRoute>
                    <Journal />
                  </SubscriptionProtectedRoute>
                } 
              />
              <Route 
                path="/instant-wellness" 
                element={
                  <SubscriptionProtectedRoute>
                    <InstantWellness />
                  </SubscriptionProtectedRoute>
                } 
              />
              <Route 
                path="/intelligent-meditation" 
                element={
                  <SubscriptionProtectedRoute>
                    <IntelligentMeditation />
                  </SubscriptionProtectedRoute>
                } 
              />
              <Route 
                path="/progress" 
                element={
                  <SubscriptionProtectedRoute>
                    <ProgressDashboard />
                  </SubscriptionProtectedRoute>
                } 
              />
              <Route 
                path="/get-tokens" 
                element={
                  <ProtectedRoute>
                    <GetTokens />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/human-therapists" 
                element={
                  <ProtectedRoute>
                    <HumanTherapists />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/leaderboard" 
                element={
                  <SubscriptionProtectedRoute>
                    <Leaderboard />
                  </SubscriptionProtectedRoute>
                } 
              />
              <Route 
                path="/challenges" 
                element={
                  <SubscriptionProtectedRoute>
                    <Challenges />
                  </SubscriptionProtectedRoute>
                } 
              />
              <Route 
                path="/mind-games" 
                element={
                  <SubscriptionProtectedRoute>
                    <MindGames />
                  </SubscriptionProtectedRoute>
                } 
              />
              <Route 
                path="/memory-game" 
                element={
                  <SubscriptionProtectedRoute>
                    <MemoryGamePage />
                  </SubscriptionProtectedRoute>
                } 
              />
              {/* Public routes */}
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              
              {/* Targeted Landing Pages */}
              <Route path="/anxiety-relief" element={<AnxietyLandingPage />} />
              <Route path="/life-transformation" element={<TransformationLandingPage />} />
              <Route path="/relationship-healing" element={<RelationshipsLandingPage />} />
              <Route path="/career-future-proofing" element={<CareerLandingPage />} />
              <Route path="/burnout-to-breakthrough" element={<BurnoutLandingPage />} />
              
              {/* Mobile-Optimized Landing Page */}
              <Route path="/mobile" element={<MobileOptimizedLanding />} />
              <Route path="/m" element={<MobileOptimizedLanding />} />
              
              {/* Male-Targeted Landing Pages */}
              <Route path="/mens-anxiety-relief" element={<MaleAnxietyLandingPage />} />
              <Route path="/mens-life-transformation" element={<MaleTransformationLandingPage />} />
              
              {/* Main Landing page for unauthenticated users, protected routes for authenticated users */}
              <Route path="/" element={<LandingPage />} />
              {/* Changed default to landing page for better conversion */}
            </Routes>
          </Suspense>
        </Box>
      </ErrorBoundary>
    </ChallengeNotificationProvider>
  );
};

export default App;
